import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';

// import { useAnalytics } from '@restworld/utils-common';
import { WorkerSignupAcceptance, signupAcceptanceErrorOf, useWorkersService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';

import { useJobPositionId } from '../../../hooks';
import { socialSignupDetails } from '../../../context/auth-flow';
// import { jobPositionApiDataAtom } from '../../job-position/job-position.atoms';
import {
  doesSizeExceedsAtom,
  isCVUploadProcessCompletedAtom,
  spontaneousApplyDataAtom,
  uploadCVLoadingAtom,
  uploadFilesAtom,
  workerEntryPointErrorsAtom,
  workerIdAtom,
} from '../worker-entry-point.atoms';
import { CV_UPLOAD_NETWORK_FAILURE_CODE, isUploadNetworkFailure } from '../cv-upload-failure';

export const useEntryPoint = () => {
  const jobPositionId = useJobPositionId();
  // const jobPositionApiData = useAtomValue(jobPositionApiDataAtom);
  const setSizeExceeds = useSetAtom(doesSizeExceedsAtom);
  const uploadCVLoadingToggler = useSetAtom(uploadCVLoadingAtom);
  const setCVUploadedProcessComplete = useSetAtom(isCVUploadProcessCompletedAtom);
  const setWorkerId = useSetAtom(workerIdAtom);
  const setSpontaneousApplyData = useSetAtom(spontaneousApplyDataAtom);
  const uploadFiles = useAtomValue(uploadFilesAtom);
  const setErrors = useSetAtom(workerEntryPointErrorsAtom);
  const { initializeAuthentication } = useAuth();

  const workerService = useWorkersService();
  // Google sign-up profile (verified at sign-in); forwarded to override the CV parse.
  const socialDetails = useAtomValue(socialSignupDetails);
  const setSocialDetails = useSetAtom(socialSignupDetails);
  // const { fbTrackWorkerStartsApplication } = useAnalytics();

  // `acceptance`: the boxes ticked on the CV upload screen. The backend keeps it with the pending
  // data, so the second call (`POST /workers/cv`, missing fields) does not need to send it again.
  const onUploadFileChange = useCallback(
    async (acceptance?: WorkerSignupAcceptance) => {
      if (uploadFiles?.[0]) {
        if (uploadFiles[0].size / 1000000 > 20) {
          setSizeExceeds(true);
          return;
        }
        // fbTrackWorkerStartsApplication();
        try {
          // A retry must not keep showing the reason the previous attempt failed.
          setErrors({});
          uploadCVLoadingToggler(true);
          const response = await workerService.uploadCV({
            cv: uploadFiles[0],
            workerId: '',
            jobPositionId: jobPositionId,
            // New-worker Google sign-up only: hand the backend the verified Google ID
            // token; it re-verifies it and derives email/name/google_id server-side,
            // never trusting raw client fields.
            idToken: socialDetails?.id_token || undefined,
            acceptance,
          });
          setWorkerId(response?.data?.user_id);
          setCVUploadedProcessComplete(true);
          // Consumed the Google profile; clear it so it can't attach to a later upload.
          if (socialDetails?.email || socialDetails?.google_id || socialDetails?.id_token) {
            setSocialDetails({ email: null, name: null, google_id: null, id_token: null });
          }
          setTimeout(() => {
            setSpontaneousApplyData((p) => ({
              ...p,
              showCoverLetter: true,
            }));
          }, 2000);
          // No page reload here, deliberately. One was added on 2025-07-14 "refresh page after
          // lead user uploads cv", and two days later a guard on `isAuthenticated` was added to
          // narrow it. `isAuthenticated` is captured in this closure and read after the session
          // has just been established, so it always holds the value from before the upload: the
          // reload never fired for the lead it was written for, and fired only for a worker who
          // was already signed in. Fourteen months of nobody missing it says the lead does not
          // need it, and the worker who was already signed in was getting their page pulled out
          // from under them a second after uploading a CV.
          if (response?.data?.jwt) {
            await initializeAuthentication(response.data.jwt);
          }
        } catch (err: any) {
          // A refusal of the boxes is not a CV problem: no status code, so the modal stays on the
          // upload screen (a 4xx status would open the missing-data form) and shows the message.
          const acceptanceError = signupAcceptanceErrorOf(err);
          if (acceptanceError) {
            setErrors({ error_code: acceptanceError });
            setCVUploadedProcessComplete(false);
            uploadCVLoadingToggler(false);
            return err;
          }
          if (isUploadNetworkFailure(err)) setErrors({ error_code: CV_UPLOAD_NETWORK_FAILURE_CODE });
          if (err?.['message']?.includes('500')) setErrors({ ...err?.data, statusCode: 500 });
          if (err?.['data']) setErrors({ ...err?.data, statusCode: err.code });
          setCVUploadedProcessComplete(false);
          uploadCVLoadingToggler(false);
          if (err?.code === 413) setSizeExceeds(true);
          return err;
        }
      }
    },
    [
      uploadFiles,
      setSizeExceeds,
      uploadCVLoadingToggler,
      workerService,
      jobPositionId,
      setWorkerId,
      setCVUploadedProcessComplete,
      setSpontaneousApplyData,
      initializeAuthentication,
      setErrors,
      socialDetails,
      setSocialDetails,
    ]
  );
  return {
    onUploadFileChange,
  };
};
